import {
  Attachment,
  Organization,
  Patient,
  Practitioner,
  PractitionerRole,
  Professional,
  ResourceList,
} from '@domain/types';
import { isOrganization, isPractitioner, isPractitionerRole } from '@domain/validators';
import { createReducer, PayloadAction } from '@reduxjs/toolkit';

import { updateEntitiesAction } from '@store/documents/documents.actions';

import { FetchIdentitiesSuccess, fetchIdentitiesSuccess } from '../user/user.actions';

import { Entities, EntitiesState, INITIAL_ENTITIES_STATE } from './entities.state';

function setProfessionals(state: EntitiesState, action: PayloadAction<ResourceList<Professional>>) {
  const { list = [] } = action.payload;

  if (list.length === 0) {
    return state;
  }

  return list.reduce((newState: EntitiesState, professional: Professional) => {
    if (isPractitioner(professional)) {
      state.Practitioner[professional.id] = professional;
    }

    if (isOrganization(professional)) {
      state.Organization[professional.id] = professional;
    }

    if (isPractitionerRole(professional)) {
      state.PractitionerRole[professional.id] = professional;
    }

    return newState;
  }, state);
}

function setResources(
  state: EntitiesState,
  entities: {
    patients?: Patient[];
    practitioners?: Practitioner[];
    organizations?: Organization[];
    practitionerRoles?: PractitionerRole[];
    attachments?: Attachment[];
  },
): EntitiesState {
  setPatientsStateFromEntitesPatient(state, entities.patients);
  setOrganizationsStateFromEntitiesOrganization(state, entities.organizations);
  setPractRoleStateFromEntites(state, entities.practitionerRoles);
  setPractAndPractRoleStateFromEntites(state, entities.practitioners);
  setAttachmentsStateFromEntitesAttachments(state, entities.attachments);
  return state;
}

export const entitiesReducer = createReducer(INITIAL_ENTITIES_STATE, {
  /* GLOBAL */
  [fetchIdentitiesSuccess.type]: (state, action: ReturnType<FetchIdentitiesSuccess>) => {
    setProfessionals(state, {
      ...action,
      payload: { list: action.payload as Professional[], total: 0 },
    });
  },
  [updateEntitiesAction.type]: (state, action: ReturnType<typeof updateEntitiesAction>) =>
    setResources(state, action.payload),
});
function setPatientsStateFromEntitesPatient(state: EntitiesState, patients: Patient[] | undefined) {
  if (patients) {
    for (const patient of patients) {
      state.Patient[patient.id] = patient;
    }
  }
}
function setOrganizationsStateFromEntitiesOrganization(
  state: EntitiesState,
  organizations: Organization[] | undefined,
) {
  if (organizations && organizations.length > 0) {
    for (const organization of organizations) {
      state.Organization[organization.id] = organization;
    }
  }
}
function setPractRoleStateFromEntites(
  state: EntitiesState,
  practitionerRoles: PractitionerRole[] | undefined,
) {
  if (practitionerRoles && practitionerRoles.length > 0) {
    for (const practitionerRole of practitionerRoles) {
      state.PractitionerRole[practitionerRole.id] = practitionerRole;
    }
  }
}
function setPractAndPractRoleStateFromEntites(
  state: EntitiesState,
  practitioners: Practitioner[] | undefined,
) {
  if (practitioners && practitioners.length > 0) {
    for (const practitioner of practitioners) {
      state.Practitioner[practitioner.id] = practitioner;
      if (practitioner.practitionerRoles && practitioner.practitionerRoles.length > 0) {
        for (const practitionerRole of practitioner.practitionerRoles) {
          state.PractitionerRole[practitionerRole.id] = practitionerRole;
        }
      }
    }
  }
}
function setAttachmentsStateFromEntitesAttachments(
  state: EntitiesState,
  attachments: Attachment[] | undefined,
) {
  if (!attachments) return;

  state.Attachments = attachments.reduce(
    (acc, attachment) => {
      const documentId = attachment.parentRefId as string;
      if (!Object.hasOwn(acc, documentId)) acc[documentId] = [];
      acc[documentId].push(attachment);
      return acc;
    },
    {} as Entities<Attachment[]>,
  );
}
